import { caixaAlta } from '@/lib/utils';
import { useQuery } from '@tanstack/react-query';
import { CalendarDays } from 'lucide-react';
import { api } from '@/lib/api';
import { EstadoErro, EstadoVazio } from '@/componentes/ui/estados';
import { Cartao, Esqueleto, Pilula } from '@/componentes/ui/primitivos';
import { moeda, type Status } from './tipos';

interface Agendamento {
  agendamento: number;
  data: string;
  janela: string | null;
  modalidade: string;
  transportadora: string | null;
  destino: string;
  tipo: string;
  notas: { chave: string; numero: number | null; fornecedor: string | null; valor: number | null; status: Status }[];
}

/**
 * A grade de recebimento — o Excel que hoje a logística mantém à mão (PH,
 * 27/10/2026). Duas semanas a partir de hoje, um bloco por dia, e em cada dia
 * as cargas por destino. Nota que já chegou fica marcada, não some: a grade é
 * também o registro do que foi recebido no dia.
 */
export function Grade({ aoAbrir }: { aoAbrir: (chave: string) => void }): JSX.Element {
  const consulta = useQuery<{ agendamentos: Agendamento[] }>({
    queryKey: ['entradas-grade'],
    queryFn: () => api.get('/api/entradas/grade'),
    refetchInterval: 120_000,
  });
  if (consulta.isError) return <EstadoErro erro={consulta.error} aoTentarDeNovo={() => void consulta.refetch()} />;
  if (consulta.isLoading) return <Esqueleto className="h-48" />;
  const lista = consulta.data?.agendamentos ?? [];
  if (!lista.length) {
    return (
      <Cartao>
        <EstadoVazio icone={<CalendarDays className="h-6 w-6" />} titulo="Nada agendado nas próximas duas semanas" />
      </Cartao>
    );
  }
  const porDia = new Map<string, Agendamento[]>();
  for (const a of lista) porDia.set(a.data, [...(porDia.get(a.data) ?? []), a]);

  return (
    <div className="space-y-4">
      {[...porDia].map(([dia, ags]) => (
        <Cartao key={dia} className="overflow-hidden">
          <div className="flex items-center justify-between border-b border-borda bg-superficie2 px-4 py-2.5">
            <p className="text-sm font-bold capitalize">
              {new Date(`${dia}T12:00:00`).toLocaleDateString('pt-BR', { weekday: 'long', day: '2-digit', month: '2-digit' })}
            </p>
            <span className="text-xs text-textoFraco">
              {ags.reduce((s, a) => s + a.notas.length, 0)} nota(s) · {moeda(ags.reduce((s, a) => s + a.notas.reduce((x, n) => x + (n.valor ?? 0), 0), 0))}
            </span>
          </div>
          <ul className="divide-y divide-borda">
            {ags.map((a) => (
              <li key={a.agendamento} className="px-4 py-3">
                <div className="flex flex-wrap items-center gap-2 text-[13px]">
                  <b>{a.destino}</b>
                  <Pilula tom={a.tipo === 'FULL' ? 'alerta' : 'marca'}>{a.tipo === 'FULL' ? 'Full' : 'Armazém'}</Pilula>
                  <span className="text-textoFraco">
                    {a.janela ?? 'sem janela'} · {a.modalidade === 'COLETA' ? 'coleta' : 'entrega'}
                    {a.transportadora ? ` · ${a.transportadora}` : ''}
                  </span>
                </div>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {a.notas.map((n) => {
                    const chegou = ['em_conferencia', 'estocada', 'disponivel_venda', 'divergencia_recebimento'].includes(n.status);
                    return (
                      <button
                        key={n.chave}
                        type="button"
                        onClick={() => aoAbrir(n.chave)}
                        className="rounded-lg border border-borda px-2.5 py-1 text-xs hover:bg-superficie2"
                      >
                        NF {n.numero} · {caixaAlta(n.fornecedor)?.slice(0, 22)} {chegou ? '✓' : n.status === 'aguardando_transferencia' ? '· falta transferência' : ''}
                      </button>
                    );
                  })}
                </div>
              </li>
            ))}
          </ul>
        </Cartao>
      ))}
    </div>
  );
}
